/** Gender identity + discovery matching (who you are / who you want to see). */

export type GenderIdentity = 'male' | 'female' | 'nonbinary' | 'other'

export type GenderInterest = GenderIdentity | 'everyone'

export const GENDER_IDENTITY_OPTIONS: Array<{ value: GenderIdentity; label: string }> = [
  { value: 'female', label: 'Woman' },
  { value: 'male', label: 'Man' },
  { value: 'nonbinary', label: 'Non-binary' },
  { value: 'other', label: 'Other' },
]

export const GENDER_INTEREST_OPTIONS: Array<{ value: GenderInterest; label: string }> = [
  { value: 'male', label: 'Men' },
  { value: 'female', label: 'Women' },
  { value: 'nonbinary', label: 'Non-binary people' },
  { value: 'everyone', label: 'Everyone' },
]

export const GENDER_IDENTITY_LABEL: Record<GenderIdentity, string> = {
  female: 'Woman',
  male: 'Man',
  nonbinary: 'Non-binary',
  other: 'Other',
}

export function parseInterestedIn(raw: string | null | undefined): GenderInterest[] {
  if (!raw || raw === 'everyone') return ['everyone']
  try {
    const parsed = JSON.parse(raw) as unknown
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.filter((v): v is GenderInterest => typeof v === 'string')
    }
  } catch {
    /* legacy token */
  }
  if (raw === 'male' || raw === 'female' || raw === 'nonbinary' || raw === 'other') {
    return [raw]
  }
  return ['everyone']
}

export function serializeInterestedIn(values: GenderInterest[]): string {
  if (!values.length || values.includes('everyone')) return 'everyone'
  return JSON.stringify([...new Set(values)])
}

/** Mutual gender preference check for discovery. Unknown genders stay permissive. */
export function gendersCompatible(
  myGender: string | null | undefined,
  myInterestedIn: GenderInterest[],
  theirGender: string | null | undefined,
  theirInterestedIn: GenderInterest[],
): boolean {
  const mine = myInterestedIn.length ? myInterestedIn : (['everyone'] as GenderInterest[])
  const theirs = theirInterestedIn.length ? theirInterestedIn : (['everyone'] as GenderInterest[])

  const iWantThem =
    !theirGender ||
    mine.includes('everyone') ||
    mine.includes(theirGender as GenderIdentity)

  const theyWantMe =
    !myGender ||
    theirs.includes('everyone') ||
    theirs.includes(myGender as GenderIdentity)

  return iWantThem && theyWantMe
}
